import {
    Alert,
    Button,
    CheckBox,
    ConfirmModal,
    DataTable,
    Icon,
    Loading,
    Spacer,
    StyleFunction,
    Surface,
    useAuth,
    useMerchantConfig,
    useModal,
    usePagination,
    useSearch,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    useProductsDeleteMutation,
    useUpdateViProductsMutation,
    useProductsQuery,
    GQProduct,
} from "graphql-sdk";
import { SearchDefinition, StringSearch, formatAmount } from "lib";
import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "locales";
import { View } from "react-native";

import { useBarcodeScannerWeb } from "../../hooks/use-barcode-scanner-web";
import { useHandleMutationError } from "../../hooks/use-handle-mutation-error";
import { RootStackScreenProps } from "../../navigation";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";
import { ImportProductsModal } from "./import-modal";
import { ProductRow } from "./product-table-row";

import type { AvailableLocale } from "locales";
import {
    useAccessToken,
    useMultiSelect,
    useSearchDefinition,
} from "../../hooks";
import produce from "immer";
import { useFocusEffect } from "@react-navigation/native";
type ScreenProps = RootStackScreenProps<"PRODUCTS">;

export function ProductListScreen({ navigation: { navigate } }: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.product");

    const hasFocusedOnce = useRef(false);
    const merchantConfig = useMerchantConfig();
    const [t] = useTranslation();
    const { createSearchDefinition } = useSearchDefinition();
    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "name",
    });

    const theme = useTheme();
    const [updateViProducts] = useUpdateViProductsMutation();

    const styles = useThemedStyle(styleFunc);
    const sharedStyles = useThemedStyle(sharedStyleFunc);

    const formRef = useRef<HTMLFormElement>(null);

    const defaultProductSearch = useMemo<SearchDefinition>(
        () => ({
            name: createSearchDefinition("name"),
            group: {
                name: t("searching.product_group", "Product group"),
                type: "string",
                value: "",
                enabled: false,
            },
            eid: {
                name: t("searching.external_id", "External ID"),
                type: "string",
                value: "",
                enabled: false,
            },
            amount: createSearchDefinition("amount"),
            barcode: {
                name: t("searching.barcode", "Barcode"),
                type: "string",
                value: "",
                enabled: false,
            },
            plu: {
                name: t("searching.plu", "PLU"),
                type: "integer",
                value: 0,
                enabled: false,
            },
        }),
        [createSearchDefinition, t]
    );

    const {
        component: searchComponent,
        indicator: searchIndicator,
        search,
        values,
        setValue,
    } = useSearch(defaultProductSearch, {
        visible: true,
        onSubmit: () => {
            // When the search form is submitted, then return the pagination to the first page.
            onPageChange(0);
        },
    });

    const {
        selectedItems: selectedProducts,
        setSelectedItems,
        handleMultiSelect,
        allItemsSelected: allRowsSelected,
        setAllItemsSelected: setAllRowsSelected,
    } = useMultiSelect<GQProduct["id"]>();

    const selectedProductsSet = useMemo(
        () => new Set(selectedProducts),
        [selectedProducts]
    );

    const { render } = useModal();
    const [{ locale }] = useAdminSession(["locale"]);
    const accessToken = useAccessToken();

    // The barcode scanner allows the user to scan for products.
    useBarcodeScannerWeb({
        onDone: (barcode: string) => {
            if (barcode !== "" && values !== null) {
                setValue(
                    "barcode",
                    produce(values.barcode, draft => {
                        (draft as StringSearch).value = barcode;
                    })
                );
            }
        },
    });

    // GraphQL
    const { data, loading, error, refetch } = useProductsQuery({
        variables: {
            includeInactive: true,
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            searching: search,
            includeChildren: true,
        },
        fetchPolicy: "cache-and-network",
    });
    const [deleteProducts] = useProductsDeleteMutation();
    const { handleMutationError } = useHandleMutationError();

    const productAmounts = useMemo(() => {
        if (!data?.products.data || !merchantConfig.currency || !locale) {
            return new Map<string, string>();
        }
        const map = new Map<string, string>();
        for (const item of data.products.data) {
            map.set(
                item.id,
                formatAmount(item.amount, merchantConfig.currency, {
                    locale: locale as AvailableLocale,
                })
            );
        }
        return map;
    }, [data?.products.data, merchantConfig.currency, locale]);

    useFocusEffect(
        useCallback(() => {
            if (hasFocusedOnce.current) {
                refetch();
            } else {
                hasFocusedOnce.current = true;
            }
        }, [refetch])
    );

    // Actions
    const handleEdit = useCallback(
        (id: string) => {
            navigate("PRODUCT_EDIT", {
                id,
            });
        },
        [navigate]
    );

    const deleteSelectedProducts = useCallback(async () => {
        const deleteResponse = await render(onClose => (
            <ConfirmModal
                headerText={t("backoffice.products.delete", "Delete products?")}
                bodyText={t(
                    "backoffice.products.delete_explain",
                    "If you choose to delete these products, any buttons that are added to any of your layouts, which use these products, will be removed.\nThis cannot be undone.\n\nAre you sure, you want to delete these products?"
                )}
                onClose={onClose}
            />
        ));

        if (!deleteResponse) {
            return;
        }

        await handleMutationError(
            async () =>
                await deleteProducts({ variables: { ids: selectedProducts } }),
            t("backoffice.products.deleted", "Products deleted"),
            async () => {
                await refetch();
            }
        );

        setSelectedItems([]);
        setAllRowsSelected(false);
    }, [
        deleteProducts,
        handleMutationError,
        refetch,
        render,
        selectedProducts,
        setAllRowsSelected,
        setSelectedItems,
        t,
    ]);

    const handleImportPress = useCallback(async () => {
        await render(onClose => <ImportProductsModal onClose={onClose} />);
    }, [render]);

    const handleSyncInventoryPress = useCallback(async () => {
        await handleMutationError(
            async () => await updateViProducts(),
            t("backoffice.products.vi_products_updated", "VI products updated")
        );
    }, [handleMutationError, t, updateViProducts]);

    function handleDownloadPress() {
        if (!formRef.current) {
            // DOM Form not ready
            return;
        }

        if (selectedProducts.length) {
            selectedProducts.forEach(id => {
                const hiddenInput = document.createElement("input");
                hiddenInput.type = "hidden";
                hiddenInput.name = "selectedProducts[]";
                hiddenInput.value = id;
                formRef.current?.appendChild(hiddenInput);
            });
        }
        formRef.current.submit();
    }

    const selectedCountText = useMemo(() => {
        if (selectedProducts.length === 0) {
            return "";
        }

        return ` (${selectedProducts.length})`;
    }, [selectedProducts]);

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error.message,
                    }
                )}
            </Alert>
        );
    }

    return (
        <AdminContainer testID="productListScreen">
            <View style={styles.container}>
                <View style={styles.actionButtonContainer}>
                    <Button
                        variant="invert"
                        disabled={!selectedProducts.length}
                        onPress={deleteSelectedProducts}
                        testID="deleteProduct"
                    >
                        {`${t("common.delete", "Delete")}${selectedCountText}`}
                    </Button>
                    <Spacer space={1} />
                    <Button
                        variant="invert"
                        onPress={handleImportPress}
                        testID="importProducts"
                    >
                        {t("backoffice.products.import", "Import")}
                    </Button>
                    <Spacer space={1} />
                    {auth.may("merchant.vi") ? (
                        <>
                            <Button
                                variant="invert"
                                onPress={handleSyncInventoryPress}
                                testID="syncInventory"
                            >
                                {t(
                                    "backoffice.products.sync_inventory",
                                    "Update inventory"
                                )}
                            </Button>
                            <Spacer space={1} />
                        </>
                    ) : null}
                    <Button
                        variant="invert"
                        onPress={handleDownloadPress}
                        testID="exportProducts"
                    >
                        {`${t(
                            "backoffice.products.export",
                            "Eksport"
                        )}${selectedCountText}`}
                    </Button>
                    <Spacer space={1} />
                    <Button
                        type="secondary"
                        onPress={() => {
                            navigate("PRODUCT_CREATE");
                        }}
                        testID="createProduct"
                    >
                        {t("common.create", "Create")}
                    </Button>
                    <Spacer space={1} />
                    {searchIndicator}
                </View>
            </View>
            <Spacer space={2} />
            <Surface>
                {searchComponent}
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title style={sharedStyles.iconColumn}>
                            <CheckBox
                                value={allRowsSelected}
                                onValueChange={value => {
                                    const allItemIds = data.products.data.map(
                                        p => p.id
                                    );
                                    handleMultiSelect(value, allItemIds);
                                    setAllRowsSelected(value);
                                }}
                            />
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "active" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("active")}
                            style={sharedStyles.activeColumn}
                        >
                            {t("backoffice.common.active", "Active")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "name" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("name")}
                            style={sharedStyles.productNameColumn}
                        >
                            {t("common.name", "Name")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "externalId"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("externalId")}
                        >
                            {t("backoffice.product.external_id", "EID")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "groupName"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("groupName")}
                        >
                            {t("backoffice.product.group", "Group")}
                        </DataTable.Title>
                        <DataTable.Title
                            style={sharedStyles.externalColumn}
                            sortDirection={
                                sortBy === "isExternal"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("isExternal")}
                        >
                            {t("backoffice.product.is_external", "External")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "plu" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("plu")}
                            numeric
                        >
                            {t("backoffice.product.plu", "PLU")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "amount" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("amount")}
                            numeric
                        >
                            {t(
                                "backoffice.product.price_incl_vat",
                                "Price incl VAT"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            style={sharedStyles.vatColumn}
                            sortDirection={
                                sortBy === "vatId" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("vatId")}
                            numeric
                        >
                            {t("backoffice.product.vat", "VAT")}
                        </DataTable.Title>
                        <DataTable.Title
                            numeric
                            style={sharedStyles.discountTemplateColumn}
                        >
                            {t(
                                "backoffice.product.discount_templates",
                                "Discounts"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            numeric
                            style={sharedStyles.quantityColumn}
                        >
                            {t(
                                "backoffice.product.inventory_quantity",
                                "Quantity"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            numeric
                            style={sharedStyles.iconColumn}
                        >
                            <Icon name="sort" color={styles.icon.color} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? (
                        <Loading />
                    ) : (
                        data.products.data.map((item, index) => {
                            const selected = selectedProductsSet.has(item.id);

                            return (
                                <ProductRow
                                    key={item.id}
                                    item={item}
                                    formattedAmount={
                                        productAmounts.get(item.id) ??
                                        formatAmount(
                                            item.amount,
                                            merchantConfig.currency,
                                            {
                                                locale: locale as AvailableLocale,
                                            }
                                        )
                                    }
                                    onSelect={handleEdit}
                                    selectedProduct={selected}
                                    onMultiSelectChange={handleMultiSelect}
                                    productId={item.id}
                                    style={
                                        index % 2 === 1
                                            ? theme.styles.oddRow
                                            : undefined
                                    }
                                    locale={locale as AvailableLocale}
                                />
                            );
                        })
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.products.pagination.pages}
                        itemCount={data?.products.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
            <form
                target="_blank"
                ref={formRef}
                action="/api/export/product"
                method="post"
            >
                <input type="hidden" name="language" value={locale} />
                <input
                    type="hidden"
                    name="token"
                    id="token"
                    value={accessToken}
                />
            </form>
        </AdminContainer>
    );
}

export const sharedStyleFunc: StyleFunction = theme => ({
    // `flex: 0` is a shortcut for `flexGrow: 0; flexShrink: 0`, which is used a lot of other places.

    iconColumn: {
        flex: 0,
        flexBasis: theme.spacingScale * 4,
    },
    activeColumn: {
        flex: 0,
        flexBasis: theme.spacingScale * 6,
    },
    productNameColumn: { flexGrow: 1, flexBasis: 150 },
    externalColumn: { flexGrow: 0, flexBasis: theme.spacingScale * 8 },
    vatColumn: {
        flex: 0,
        flexBasis: theme.spacingScale * 8,
    },
    quantityColumn: {
        flex: 0,
        flexBasis: theme.spacingScale * 8,
    },
    discountTemplateColumn: {
        flex: 0,
        flexBasis: theme.spacingScale * 9,
    },
});

const styleFunc: StyleFunction = theme => ({
    container: {
        justifyContent: "space-between",
        flexWrap: "wrap",
    },
    actionButtonContainer: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
    icon: {
        color: theme.colors.black,
    },
});
